/**
 * 乗船開始・下船の自己登録カード（配乗漏れ防止）。
 *
 * 陸上の配乗登録が漏れていると勤怠を登録できないため、船員自身が乗船開始を登録できるようにする。
 * 登録内容は必ず陸上の管理者へ通知される（サーバー側で通知）。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/api';
import { hasFeature } from '../utils/featureFlags';

interface Vessel { id: string; vessel_name: string; last_boarded_at: string | null }
interface Current { id: string; vessel_id: string; vessel_name: string; boarded_at: string; position: string | null }

export const SelfBoardingCard: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const nav = useNavigate();
  const [current, setCurrent] = useState<Current | null>(null);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    apiFetch<{ data?: { current: Current | null; vessels: Vessel[] } }>('/self-boarding/status')
      .then(r => { setCurrent(r?.data?.current ?? null); setVessels(r?.data?.vessels ?? []); })
      .catch(() => { /* 使えない会社では出さない */ })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const board = async (v: Vessel) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      await apiFetch('/self-boarding/board', { method: 'POST', body: JSON.stringify({ vessel_id: v.id }) });
      setMsg(`「${v.vessel_name}」への乗船を登録しました。陸上の管理者へ通知しました。`);
      setPicking(false);
      load();
      onChanged?.();
    } catch (e: any) { setErr(e?.message ?? '登録に失敗しました'); }
    finally { setBusy(false); }
  };

  const disembark = async () => {
    if (!window.confirm(`「${current?.vessel_name}」から下船したことを登録します。\n（陸上の管理者へ通知されます）`)) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await apiFetch('/self-boarding/disembark', { method: 'POST' });
      setMsg('下船を登録しました。陸上の管理者へ通知しました。');
      load();
      onChanged?.();
    } catch (e: any) { setErr(e?.message ?? '登録に失敗しました'); }
    finally { setBusy(false); }
  };

  if (loading) return null;

  return (
    <div style={{ width: '100%', background: current ? '#f0fdf4' : '#fffbeb', border: `1.5px solid ${current ? '#bbf7d0' : '#fde68a'}`, borderRadius: 16, padding: '14px 16px' }}>
      {picking ? (
        <>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#92400e', marginBottom: 8 }}>どの船に乗りますか？</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
            {vessels.map(v => (
              <button key={v.id} onClick={() => board(v)} disabled={busy}
                style={{ width: '100%', padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#fff', color: '#0f172a', fontSize: 15, fontWeight: 800, cursor: busy ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
                🚢 {v.vessel_name}
                {v.last_boarded_at && <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginLeft: 8 }}>前回 {String(v.last_boarded_at).slice(0, 10)}</span>}
              </button>
            ))}
            {vessels.length === 0 && <div style={{ fontSize: 13, color: '#94a3b8' }}>選べる船がありません。管理者にご連絡ください。</div>}
          </div>
          <button onClick={() => setPicking(false)}
            style={{ width: '100%', padding: '11px', borderRadius: 12, border: 'none', background: 'transparent', color: '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 8 }}>
            やめる
          </button>
        </>
      ) : (
        <>
          {/* いまの状態 */}
          {current ? (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#166534', marginBottom: 4 }}>🚢 {current.vessel_name} に乗船中</div>
              <div style={{ fontSize: 12, color: '#15803d', lineHeight: 1.6, marginBottom: 10 }}>
                乗船開始 {String(current.boarded_at).slice(0, 10)}
                {current.position ? ` ／ ${current.position}` : ''}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#92400e', marginBottom: 4 }}>⚠️ 乗船の登録がありません</div>
              <div style={{ fontSize: 12, color: '#a16207', lineHeight: 1.6, marginBottom: 10 }}>
                このままでは勤怠を登録できません。乗船したら「乗船登録をする」を押してください（陸上の管理者へ通知されます）。
              </div>
            </>
          )}

          {/* 乗船・下船の登録ボタンは常に並べて出す（今できる方だけ押せる） */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button onClick={() => setPicking(true)} disabled={busy || !!current}
              style={{ padding: '14px 8px', borderRadius: 12, border: 'none', fontSize: 14, fontWeight: 800,
                background: current ? '#e2e8f0' : 'linear-gradient(145deg,#0057d9,#003fa3)',
                color: current ? '#94a3b8' : '#fff', cursor: current ? 'not-allowed' : 'pointer' }}>
              乗船登録をする
            </button>
            <button onClick={disembark} disabled={busy || !current}
              style={{ padding: '14px 8px', borderRadius: 12, fontSize: 14, fontWeight: 800,
                border: current ? '1.5px solid #fecaca' : '1.5px solid #e2e8f0',
                background: '#fff', color: current ? '#dc2626' : '#cbd5e1',
                cursor: current ? 'pointer' : 'not-allowed' }}>
              下船登録をする
            </button>
          </div>

          {/* 乗下船表の確認もここにまとめる */}
          {hasFeature('crew_boarding_sheet') && (
            <button onClick={() => nav('/boarding-sheet')}
              style={{ width: '100%', marginTop: 8, padding: '13px', borderRadius: 12, border: '1.5px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
              📅 乗下船表を確認する
            </button>
          )}
        </>
      )}
      {msg && <div style={{ fontSize: 12, color: '#166534', fontWeight: 700, marginTop: 10 }}>{msg}</div>}
      {err && <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 700, marginTop: 10 }}>{err}</div>}
    </div>
  );
};
